import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  AttachmentToolCall,
  BaseProgressToolCall,
  DeepResearchToolType,
  GenerateImageToolCall,
  IdsToModelsToolCall,
  isMCPTool,
  MCPToolCall,
  PlottingToolCall,
  PublicDataSubAgentToolCall,
  QueryWarehouseToolCall,
  ReadBenchlingDocumentToolCall,
  SearchNotebookEntriesToolCall,
  SearchSequencesToolCall,
  SearchToolCall,
} from 'llm/deep-research/tool-details/types';
import {FONT_PROXIMA_12, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';

export interface BaseToolDetailProps {
  toolCall: BaseProgressToolCall;
}

/**
 * Formats the input string for SearchNotebookEntries tool calls
 */
function formatSearchNotebookEntriesInput(toolCall: SearchNotebookEntriesToolCall): string {
  const parts: Array<string> = [];

  if (toolCall.queries?.length) {
    parts.push(`Queries: ${toolCall.queries.map((q) => `"${q}"`).join(', ')}`);
  }

  if (toolCall.authoredBy?.length) {
    parts.push(`Authored By: ${toolCall.authoredBy.join(', ')}`);
  }

  if (toolCall.createdAfter) {
    parts.push(`Created After: ${toolCall.createdAfter}`);
  }

  if (toolCall.createdBefore) {
    parts.push(`Created Before: ${toolCall.createdBefore}`);
  }

  if (toolCall.modifiedAfter) {
    parts.push(`Modified After: ${toolCall.modifiedAfter}`);
  }

  if (toolCall.modifiedBefore) {
    parts.push(`Modified Before: ${toolCall.modifiedBefore}`);
  }

  if (toolCall.reviewCompletedAfter) {
    parts.push(`Review Completed After: ${toolCall.reviewCompletedAfter}`);
  }

  if (toolCall.reviewCompletedBefore) {
    parts.push(`Review Completed Before: ${toolCall.reviewCompletedBefore}`);
  }

  if (toolCall.reviewStatus) {
    parts.push(`Review Status: ${toolCall.reviewStatus}`);
  }

  if (toolCall.reviewRole) {
    parts.push(`Review Role: ${toolCall.reviewRole}`);
  }

  if (toolCall.inProjectOrFolder) {
    parts.push(`In Project/Folder: ${toolCall.inProjectOrFolder}`);
  }

  if (toolCall.inStudy) {
    parts.push(`In Study: ${toolCall.inStudy}`);
  }

  if (toolCall.offset !== undefined && toolCall.offset > 0) {
    parts.push(`Offset: ${toolCall.offset}`);
  }

  return parts.length > 0 ? parts.join('\n') : 'No search parameters';
}

/**
 * Extracts input parameters from a tool call for display
 */
function getToolInputString(toolCall: BaseProgressToolCall): string {
  // Handle MCP tools by checking the tool name format
  if (typeof toolCall.tool === 'string' && isMCPTool(toolCall.tool)) {
    const mcpToolCall = toolCall as MCPToolCall;
    const argsEntries = Object.entries(mcpToolCall.args);
    if (argsEntries.length === 0) {
      return 'No input parameters';
    }
    return argsEntries
      .map(([key, value]) => {
        if (typeof value === 'object' && value != null) {
          return `${key}: ${JSON.stringify(value, null, 2)}`;
        }
        return `${key}: ${value}`;
      })
      .join('\n');
  }

  switch (toolCall.tool) {
    case DeepResearchToolType.SearchTool: {
      const searchTool = toolCall as SearchToolCall;
      return `Query: "${searchTool.query || ''}"${searchTool.objectTypes?.length ? `\nObject Types: ${searchTool.objectTypes.join(', ')}` : ''}`;
    }
    case DeepResearchToolType.SearchNotebookEntries:
      return formatSearchNotebookEntriesInput(toolCall as SearchNotebookEntriesToolCall);
    case DeepResearchToolType.SearchSequencesTool: {
      const sequencesTool = toolCall as SearchSequencesToolCall;
      return `Query: "${sequencesTool.query || ''}"`;
    }
    case DeepResearchToolType.QueryWarehouseTool: {
      const warehouseTool = toolCall as QueryWarehouseToolCall;
      return `Query: "${warehouseTool.query || ''}"`;
    }
    case DeepResearchToolType.ReadBenchlingDocumentTool: {
      const documentTool = toolCall as ReadBenchlingDocumentToolCall;
      return `Document ID: ${documentTool.documentId || 'N/A'}`;
    }
    case DeepResearchToolType.AttachmentTool: {
      const attachmentTool = toolCall as AttachmentToolCall;
      return `Attachment ID: ${attachmentTool.attachmentId || 'N/A'}`;
    }
    case DeepResearchToolType.PublicDataSubAgent: {
      const publicDataTool = toolCall as PublicDataSubAgentToolCall;
      return `Query: "${publicDataTool.query || ''}"`;
    }
    case DeepResearchToolType.IdsToModelsTool: {
      const idsToModelsTool = toolCall as IdsToModelsToolCall;
      return `Object IDs: ${idsToModelsTool.objectIds?.join(', ')}\nInclude Relevant Items: ${idsToModelsTool.includeRelevantItems || false}`;
    }
    case DeepResearchToolType.PlottingTool: {
      const plottingTool = toolCall as PlottingToolCall;
      return plottingTool.code || 'No code provided';
    }
    case DeepResearchToolType.GenerateImageTool: {
      const generateImageTool = toolCall as GenerateImageToolCall;
      return `Prompt: "${generateImageTool.prompt || ''}"`;
    }
    case DeepResearchToolType.ResearchSubAgent:
    case DeepResearchToolType.Think:
    case DeepResearchToolType.CompleteAnswer:
    case DeepResearchToolType.GetWarehouseOverviewTool:
    default:
      // For tools without specific input parameters, show generic message
      return 'No input parameters';
  }
}

/**
 * Component for displaying detailed information about generic tool calls
 * (ResearchSubAgent, Think, CompleteAnswer, etc.).
 */
export function BaseToolDetail({toolCall}: BaseToolDetailProps): JSX.Element {
  const inputString = getToolInputString(toolCall);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Input:</div>
        <div className={css(styles.codeBlock)}>{inputString}</div>
      </div>

      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Output:</div>
        <div className={css(styles.codeBlock)}>{toolCall.responseText || 'No response available'}</div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },

  codeBlock: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    padding: '12px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.4',
    maxHeight: '250px',
    overflowY: 'auto',
  },
});
